import React from "react";
// 1. Import Helmet
import { Helmet } from "react-helmet";
import Header from "../Components/Header";
import Footer from "../Components/Footer";

// Define metadata constants for SEO
const PAGE_TITLE = "Terms of Service | Steminorder Maritime Platform User Agreement";
const PAGE_DESCRIPTION = "Review the official Terms of Service (TOS) for Steminorder. This agreement governs your use of our cargo, vessel, S/P, and time charter services.";
const CANONICAL_URL = "https://www.steminorder.com/terms-of-service";

const TermsOfService = () => {
  return (
    <>
      {/* ============================================================
        REACT HELMET INTEGRATION 
        ============================================================
      */}
      <Helmet>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESCRIPTION} />
        
        {/* Canonical URL */}
        <link rel="canonical" href={CANONICAL_URL} />

        {/* Open Graph Tags (less critical but good practice for legal pages) */}
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESCRIPTION} />
        <meta property="og:url" content={CANONICAL_URL} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Steminorder" />
      </Helmet>
      
      <Header />

      <main className="container py-16">
        <h1 className="text-3xl font-bold mb-6">Terms of Service</h1>

        <p className="text-sm text-gray-600 mb-6">
          Please read these **Terms of Service** carefully before using Steminorder.
          By using the site you agree to these terms.
        </p>

        {/* Table of contents */}
        <nav className="mb-8">
          <h2 className="font-semibold mb-2">Contents</h2>
          <ul className="list-disc list-inside text-sm text-gray-700">
            <li><a href="#cargo">Cargo</a></li>
            <li><a href="#vessel">Vessel</a></li>
            <li><a href="#sale-purchase">Sale / Purchase</a></li>
            <li><a href="#time-charter">Time Charter</a></li>
            <li><a href="#regular-lines">Regular Lines</a></li>
            <li><a href="#companies">Companies</a></li>
            <li><a href="#facilities">Facilities</a></li>
            <li><a href="#services">Services</a></li>
            <li><a href="#my-account">My Account</a></li>
            <li><a href="#templates">Templates</a></li>
            <li><a href="#faq">FAQ</a></li>
            <li><a href="#hr">Human Resources</a></li>
            <li><a href="#useful-links">Useful Links</a></li>
            <li><a href="#contact">Contact Us</a></li>
            <li><a href="#copyright">Copyright & Warning</a></li>
          </ul>
        </nav>

        {/* CARGO */}
        <section id="cargo" className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">CARGO</h2>

          <article id="cargo-entry" className="mb-4">
            <h3 className="font-semibold">Cargo - Entry</h3>
            <p className="text-gray-700">
              You can enter or edit your cargo details. Since your full details
              are visible to shipbrokers and ship owners, they can directly reach
              you to supply feasible ships.
            </p>
          </article>

          <article id="cargo-search" className="mb-4">
            <h3 className="font-semibold">Cargo - Search</h3>
            <p className="text-gray-700">
              You can easily search cargoes by "Loading Region & Lay Can"
              criteria. Then, you can directly send e-mail to contact persons
              dealing with the cargo.
            </p>
          </article>
        </section>

        {/* VESSEL */}
        <section id="vessel" className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">VESSEL</h2>

          <article id="vessel-details-search" className="mb-4">
            <h3 className="font-semibold">Vessel Detail</h3>
            <h4 className="font-medium">VesselDetails - Search</h4>
            <p className="text-gray-700">
              You can find full registered vessel details by this form. Please
              fill in the blanks, then the program will display vessels matching
              the criteria.
            </p>
          </article>

          <article id="vessel-details-entry" className="mb-4">
            <h4 className="font-medium">VesselDetails - Entry</h4>
            <p className="text-gray-700">
              Once you enter your vessel details, charterers or brokers can
              reach them easily and contact people in the market interested in
              your vessel.
            </p>
          </article>

          <article id="vessel-position" className="mb-4">
            <h4 className="font-medium">Vessel Position</h4>

            <div className="mt-2">
              <h5 className="font-medium">VesselPosition - Search</h5>
              <p className="text-gray-700">
                You can search vessels' positions by entering any of name, type,
                region, port or date criteria.
              </p>
            </div>

            <div className="mt-2">
              <h5 className="font-medium">VesselPosition - Entry</h5>
              <p className="text-gray-700">
                All members can declare their vessels' positions so cargo owners
                can learn about available vessels and find suitable cargoes.
              </p>
            </div>
          </article>
        </section>

        {/* SALE / PURCHASE */}
        <section id="sale-purchase" className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">Sale / Purchase</h2>

          <article id="sale" className="mb-3">
            <h3 className="font-medium">Sale</h3>
            <p className="text-gray-700">
              You can enter your vessel's details to find the best price on the
              S/P market.
            </p>
          </article>

          <article id="purchase" className="mb-3">
            <h3 className="font-medium">Purchase</h3>
            <p className="text-gray-700">
              You can find vessels for sale on this page by stating the vessel
              criteria you want to purchase.
            </p>
          </article>
        </section>

        {/* TIME CHARTER */}
        <section id="time-charter" className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">Time Charter</h2>

          <article id="timecharterr-entry" className="mb-3">
            <h3 className="font-medium">TimeCharterR - Entry</h3>
            <p className="text-gray-700">
              You can enter the requirements of vessels that meet your needs for
              Time Charter (T/C).
            </p>
          </article>

          <article id="timecharterr-search" className="mb-3">
            <h3 className="font-medium">TimeCharterR - Search</h3>
            <p className="text-gray-700">
              You can find vessel requirements of charterers for T/C and contact
              them.
            </p>
          </article>

          <article id="tcvessel-entry" className="mb-3">
            <h3 className="font-medium">TCVessel - Entry</h3>
            <p className="text-gray-700">
              You can open your vessels for T/C by entering a date interval and
              the region/port of your vessel.
            </p>
          </article>

          <article id="tcvessel-search" className="mb-3">
            <h3 className="font-medium">TCVessel - Search</h3>
            <p className="text-gray-700">
              You can search all available vessels or filter by desired criteria
              and contact the owners.
            </p>
          </article>
        </section>

        {/* REGULAR LINES */}
        <section id="regular-lines" className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">Regular Lines</h2>

          <article id="regularlines-entry" className="mb-3">
            <h3 className="font-medium">RegularLines - Entry</h3>
            <p className="text-gray-700">
              Ship owners or managers can declare regular lines (up to 5
              countries at a time) to inform cargo owners.
            </p>
          </article>

          <article id="regularlines-search" className="mb-3">
            <h3 className="font-medium">RegularLines - Search</h3>
            <p className="text-gray-700">
              You can find regular lines between any two countries for RoRo,
              container, bulk carriers and other vessel types.
            </p>
          </article>
        </section>

        {/* COMPANIES */}
        <section id="companies" className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">COMPANIES</h2>

          <article id="company-entry" className="mb-3">
            <h3 className="font-medium">Company - Entry</h3>
            <p className="text-gray-700">
              Enter your company information and select your company concerns.
              Other members can see your details and contact you for
              cooperation.
            </p>
          </article>

          <article id="company-search" className="mb-3">
            <h3 className="font-medium">Company - Search</h3>
            <p className="text-gray-700">
              You can list full details of companies and directly contact
              managers or owners via this flexible search form.
            </p>
          </article>
        </section>

        {/* FACILITIES */}
        <section id="facilities" className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">FACILITIES</h2>

          <div className="space-y-3 text-gray-700">
            <div>
              <h4 className="font-medium">Abbreviations</h4>
              <p>Here you can find most of the abbreviations used in the shipping sector.</p>
            </div>

            <div>
              <h4 className="font-medium">Conversion Table</h4>
              <p>
                Convert measurements (e.g. miles &lt;→&gt; kilometers, liters &lt;→&gt; gallons, centigrade &lt;→&gt; Fahrenheit).
                The Calibration button provides equivalent measures; a calculator is available at the bottom of that page.
              </p>
            </div>

            <div>
              <h4 className="font-medium">Currency Converter</h4>
              <p>You may convert currency amounts for any date.</p>
            </div>

            <div>
              <h4 className="font-medium">Dictionaries</h4>
              <p>Available in 7 languages: English, German, French, Italian, Russian, Spanish and Turkish.</p>
            </div>

            <div>
              <h4 className="font-medium">Walrus Symbols & Phonetics</h4>
              <p>A useful table for walrus symbols and phonetics.</p>
            </div>

            <div>
              <h4 className="font-medium">Ports Information</h4>
              <p>Detailed information for ports around the world.</p>
            </div>

            <div>
              <h4 className="font-medium">Regions</h4>
              <p>Maps showing regions and boundaries.</p>
            </div>

            <div>
              <h4 className="font-medium">Ship Types</h4>
              <p>Basic descriptions and plans of vessel types.</p>
            </div>

            <div>
              <h4 className="font-medium">Weather Forecast</h4>
              <p>Five-day weather forecasts for countries and cities.</p>
            </div>

            <div>
              <h4 className="font-medium">World Holiday Calendar</h4>
              <p>Special, official and religious days for countries.</p>
            </div>

            <div>
              <h4 className="font-medium">World Time</h4>
              <p>Local time and location information for countries and cities.</p>
            </div>

            <div>
              <h4 className="font-medium">Worldwide Activities</h4>
              <p>Announce legal and institutional activities.</p>
            </div>
          </div>
        </section>

        {/* SERVICES */}
        <section id="services" className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">SERVICES</h2>

          <article id="activities" className="mb-3">
            <h3 className="font-medium">Activities</h3>

            <div className="mt-2">
              <h5 className="font-medium">Activity - Entry</h5>
              <p className="text-gray-700">
                Enter personal or company activities/meetings on Steminorder's board to inform others.
              </p>
            </div>

            <div className="mt-2">
              <h5 className="font-medium">Activity - List</h5>
              <p className="text-gray-700">
                See all activities on the board and email activity owners.
              </p>
            </div>
          </article>

          <article id="guestbook" className="mb-3">
            <h3 className="font-medium">Guestbook</h3>

            <div className="mt-2">
              <h5 className="font-medium">Sign Guestbook</h5>
              <p className="text-gray-700">Leave notes for Steminorder users that everyone can see.</p>
            </div>

            <div className="mt-2">
              <h5 className="font-medium">View Guestbook</h5>
              <p className="text-gray-700">Read business and personal notes from charterers, brokers and users.</p>
            </div>
          </article>

          <article id="distance-table" className="mb-3">
            <h3 className="font-medium">Distance Table</h3>
            <p className="text-gray-700">
              Retrieve port-to-port distances (including routing options via Suez, Panama, Capes, Magellan Straits). You can search ports by country and calculate multiple routes; the table will return shortest and alternative routes when applicable.
            </p>
          </article>
        </section>

        {/* MY ACCOUNT */}
        <section id="my-account" className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">MY ACCOUNT</h2>
          <p className="text-gray-700">
            Manage your account: update company information, update vessels and cargoes, and more.
          </p>
        </section>

        {/* TEMPLATES */}
        <section id="templates" className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">Templates</h2>

          <div className="space-y-3 text-gray-700">
            <div>
              <h4 className="font-medium">Annual Report</h4>
              <p>
                Use the Excel template to enter records; calculations provide automatic loss/profit and analysis. You can open online or copy to your PC.
              </p>
            </div>

            <div>
              <h4 className="font-medium">Fixture Cover</h4>
              <p>
                Template for fixture of shipment information (vessel, charterers, agency, fixture date, freight, ports, expenses, profits). Open, use online, save or print.
              </p>
            </div>

            <div>
              <h4 className="font-medium">Laytime Calculation</h4>
              <p>
                Prepare laytime statements by specifying charter-party terms and the statement of facts. The program recognizes when time counts and calculates demurrage/dispatch.
              </p>
            </div>

            <div>
              <h4 className="font-medium">Statement of Facts</h4>
              <p>
                Standard form to collect vessel activity while in port; used to run laytime calculations and estimate demurrage/dispatch values.
              </p>
            </div>

            <div>
              <h4 className="font-medium">Voyage Estimation</h4>
              <p>
                Build voyage estimates by storing vessels, employments, ports and distances. Automatically calculated distances allow estimation of voyage expenses, duration and T/C rate. Supports Ro-Ro, dry cargo and tanker.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">FAQ</h2>
          <p className="text-gray-700">Find frequently asked questions submitted by Steminorder users in this section.</p>
        </section>

        {/* HUMAN RESOURCES */}
        <section id="hr" className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">HUMAN RESOURCES</h2>

          <article id="cv-entry" className="mb-3">
            <h3 className="font-medium">Curriculum Vitae</h3>
            <p className="text-gray-700">
              <strong>CV Entry</strong> — enter your curriculum vitae to find jobs that match your qualifications.
            </p>
          </article>

          <article id="open-positions" className="mb-3">
            <h3 className="font-medium">Open Positions</h3>
            <p className="text-gray-700">
              Companies can declare open positions to reach candidates seeking jobs.
            </p>
          </article>
        </section>

        {/* USEFUL LINKS */}
        <section id="useful-links" className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">Useful Links</h2>
          <p className="text-gray-700">Many useful websites are listed by category related to shipping.</p>
        </section>

        {/* CONTACT */}
        <section id="contact" className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">Contact Us</h2>
          <p className="text-gray-700">
            Send an email or fill our feedback form. Our authorized staff will reply as soon as possible.
          </p>
        </section>

        {/* COPYRIGHT & WARNING */}
        <section id="copyright" className="mb-12">
          <h2 className="text-2xl font-semibold mb-4">Copyright</h2>
          <p className="text-gray-700 mb-3">
            All rights reserved.
          </p>

          <div className="bg-gray-50 p-4 border rounded text-sm text-gray-800">
            <strong>Warning:</strong>
            <p className="mt-2">
              This computer program is protected by copyright law and international treaties. Unauthorized reproduction
              or distribution of this program, or any portion of it, may result in severe civil and criminal penalties,
              and will be prosecuted to the maximum extent possible under the law.
            </p>
          </div>
        </section>

        <div className="text-center">
          <a href="/" className="inline-block mt-6 btn btn-primary">Back to Home</a>
        </div>
      </main>

      <Footer />
    </>
  );
};

export default TermsOfService;